import {CookieService} from '../services/cookie.service';
import {FacebookCookieService} from '../services/facebook-cookie.service';

interface ParamConfig {
    urlParam: string;
    cookieName: string;
    valueProcessor: (value: string) => string;
    overrideGuard?: (oldValue: string | undefined, newProcessedValue: string, rawValue: string) => boolean;
}

/**
 * This class is responsible for catching the params from the URL and storing them in a cookie.
 * !IMPORTANT there is a similiar/equal class for b2c, consider updating / keeping it in sync
 */
export class QueryCatcher {
    static readonly STRING_PARAMS: string[] = [
        'gclid',
        'gbraid',
        'wbraid',
        'msclkid',
        'utm_campaign',
        'utm_medium',
        'utm_source',
        'utm_content',
        'utm_term',
    ];

    static readonly PROCESSABLE_PARAMS: ParamConfig[] = [
        {
            urlParam: 'fbclid',
            cookieName: '_fbc',
            valueProcessor: (value: string): string => FacebookCookieService.formatFbcValue(value),
            overrideGuard: (oldValue: string | undefined, newProcessedValue: string, rawValue: string): boolean =>
                FacebookCookieService.shouldOverrideFbc(oldValue, newProcessedValue, rawValue)
        },
    ];

    public static saveInCookies(): void {
        const urlParams = new URLSearchParams(window.location.search);
        
        QueryCatcher.STRING_PARAMS.forEach(function (paramName) {
            const newValue = urlParams.get(paramName);
            if (newValue) {
                CookieService.setCookie(paramName, newValue, 365);
            }
        });
        
        QueryCatcher.PROCESSABLE_PARAMS.forEach(function (config) {
            const newValue = urlParams.get(config.urlParam);
            
            if (newValue) {
                const processedValue = config.valueProcessor(newValue);
                
                if (config.overrideGuard) {
                    const oldValue = CookieService.getCookie(config.cookieName);
                    if (!config.overrideGuard(oldValue, processedValue, newValue)) {
                        return;
                    }
                }
                
                CookieService.setCookie(config.cookieName, processedValue, 365);
            }
        });
    }

    /**
     * localStorage is not available/accessible in all contexts, e.g.
     *   - Safari/Firefox with cookies/storage blocked
     *   - Some private browsing modes
     * use try-catch instead of feature detection to prevent errors like "SecurityError: The operation is insecure."
     * in those contexts
     */
    public static saveInLocalStorage(): void {
        const urlParams = new URLSearchParams(window.location.search);

        QueryCatcher.STRING_PARAMS.forEach(function (paramName) {
            const newValue = urlParams.get(paramName);
            if (newValue) {
                try {
                    localStorage.setItem(paramName, newValue);
                } catch {
                    return;
                }
            }
        });

        QueryCatcher.PROCESSABLE_PARAMS.forEach(function (config) {
            const newValue = urlParams.get(config.urlParam);
            if (newValue) {
                try {
                    localStorage.setItem(config.urlParam, newValue);
                } catch {
                    return;
                }
            }
        });
    }
}

window.addEventListener('load', () => {
    QueryCatcher.saveInCookies();
    QueryCatcher.saveInLocalStorage();
});
